import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchEmployees,
  fetchJoinRequests,
  fetchJoinRequest,
  acceptJoinRequest,
  declineJoinRequest,
  sendInviteLink,
  updatePrimaryContact,
  fetchUserRoles,
  fetchGlobalUserRoles,
  createEmployee,
  type CreateEmployeeRequest,
} from '../api';
import type { EmployeeQueryParams, CreateEmployeeData } from '../types';
import { localizedReferenceDataKeys } from '@/locales/localizedReferenceData';
import { useAppLocale } from '@/locales/useAppLocale';

export const employeesKeys = {
  all: ['employees'] as const,
  lists: () => [...employeesKeys.all, 'list'] as const,
  list: (params: EmployeeQueryParams) =>
    [...employeesKeys.lists(), params] as const,
};

export const joinRequestsKeys = {
  all: ['joinRequests'] as const,
  byCompany: (companyId: string) => [...joinRequestsKeys.all, companyId] as const,
  single: (companyId: string, requestId: string) => [...joinRequestsKeys.all, companyId, requestId] as const,
};

export function useEmployees(params: EmployeeQueryParams, enabled = true) {
  return useQuery({
    queryKey: employeesKeys.list(params),
    queryFn: () => fetchEmployees(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: enabled && !!params.companyId,
  });
}

export function useJoinRequests(companyId: string) {
  return useQuery({
    queryKey: joinRequestsKeys.byCompany(companyId),
    queryFn: () => fetchJoinRequests(companyId),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: !!companyId,
  });
}

export function useJoinRequest(companyId: string, requestId?: string) {
  return useQuery({
    queryKey: joinRequestsKeys.single(companyId, requestId ?? ''),
    queryFn: () => fetchJoinRequest(companyId, requestId!),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: !!companyId && !!requestId,
  });
}

export function useAcceptJoinRequest(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { requestId: string; contact: CreateEmployeeData } | { requestId: string; contactId: string }) => {
      const { requestId, ...data } = params;
      return acceptJoinRequest(companyId, requestId, data as { contact: CreateEmployeeData } | { contactId: string });
    },
    onSuccess: (_, { requestId }) => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
      queryClient.invalidateQueries({ queryKey: joinRequestsKeys.byCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: joinRequestsKeys.single(companyId, requestId) });
    },
  });
}

export function useDeclineJoinRequest(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestId: string) => declineJoinRequest(companyId, requestId),
    onSuccess: (__, requestId) => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
      queryClient.invalidateQueries({ queryKey: joinRequestsKeys.byCompany(companyId) });
      queryClient.invalidateQueries({ queryKey: joinRequestsKeys.single(companyId, requestId) });
    },
  });
}

export function useSendInviteLink(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, email }: { contactId: string; email: string }) =>
      sendInviteLink(companyId, contactId, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export function useUpdatePrimaryContact(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contactId,
      isPrimary,
    }: {
      contactId: string;
      isPrimary: boolean;
    }) => updatePrimaryContact(companyId, contactId, isPrimary),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export const rolesKeys = {
  all: () => localizedReferenceDataKeys.userRoles,
  byCompany: (companyId: string, locale: string) =>
    [...rolesKeys.all(), companyId, locale] as const,
};

export function useUserRoles(companyId: string) {
  const locale = useAppLocale();

  return useQuery({
    queryKey: rolesKeys.byCompany(companyId, locale),
    queryFn: () => fetchUserRoles(companyId, locale),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!companyId,
  });
}

export function useCreateEmployee(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEmployeeRequest) => createEmployee(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export const globalRolesKeys = {
  all: () => localizedReferenceDataKeys.globalUserRoles,
  list: (locale: string) => [...globalRolesKeys.all(), locale] as const,
};

export function useGlobalUserRoles() {
  const locale = useAppLocale();

  return useQuery({
    queryKey: globalRolesKeys.list(locale),
    queryFn: () => fetchGlobalUserRoles(locale),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
